import { Injectable, OnDestroy } from '@angular/core';
import { isString } from 'lodash-es';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ApAlert, ApAlertType } from './ApAlert';

//
// Provides a service for showing alert messages.
// Supports displaying error, success and warning alerts.
//
// based on https://github.com/cornflourblue/angular-8-alert-notifications
//
// To use, add the <alert></alert> tag where you want alerts to be displayed.
//    <ng-container>
//      div class="col-sm-12" >
//        <ap-alert></ap-alert>
//      </div>
//    </ng-container>
//
// then add providers for ApAlertService to the component using <ap-alert></ap-alert>
//  @Component({providers: [
//      ApAlertService,
//    ],
//    ...
//
// AlertComponent optional inputs
//  fade - controls if alert messages are faded out when closed. Defaults to true.
//
// ApAlert with no optional parameters: this.alertService.success('It worked!');
// ApAlert with optional parameter: this.alertService.success('Save completed!', { autoClose: true, autoCloseTimeout: 10000 });
//

@Injectable()
export class ApAlertService implements OnDestroy {

  private clearSubject$ = new Subject<ApAlert>();
  private clearErrorSubject$ = new Subject<string>();
  private subject$ = new ReplaySubject<ApAlert>(6, 4000);  // keep last 6 alerts for 4 seconds
  readonly defaultId = 'default-alert';
  private defaultHtmlBaseId = 1;

  ngOnDestroy() {
    this.clearSubject$.complete();
    this.subject$.complete();
  }

  // Used by ApAlertComponent only
  onAlert(id = this.defaultId): Observable<ApAlert> {
    return this.subject$.asObservable().pipe(filter((x) => x && x.id === id));
  }

  // Used by ApAlertComponent only
  onClearAlert(id = this.defaultId): Observable<ApAlert> {
    return this.clearSubject$.asObservable().pipe(filter((x) => x && x.id === id));
  }

  // Used by ApAlertComponent only
  onClearErrors(id = this.defaultId): Observable<string> {
    return this.clearErrorSubject$.asObservable().pipe(filter((x) => x === id));
  }

  success(message: string, options?: Partial<ApAlert>) {
    return this.alert(new ApAlert({ ...options, type: ApAlertType.Success, message }));
  }

  info(message: string, options?: Partial<ApAlert>) {
    return this.alert(new ApAlert({ ...options, type: ApAlertType.Info, message }));
  }

  error(message: string, options?: Partial<ApAlert>) {
    return this.alert(new ApAlert({ ...options, type: ApAlertType.Error, message }));
  }

  // same as error(), but allows unknown type for message, for unknown catch blocks.
  // This was mainly added for type safety to handle cases where we were previously passing a value of type any to error(),
  // but were assuming it would always be a string
  error2(message: unknown, options?: Partial<ApAlert>) {
    return this.error(isString(message) ? message: '', options);
  }

  warn(message: string, options?: Partial<ApAlert>) {
    return this.alert(new ApAlert({ ...options, type: ApAlertType.Warning, message }));
  }

  alert(alert: ApAlert) {
    alert.id = alert.id || this.defaultId;
    alert.htmlBaseId = alert.htmlBaseId || (this.defaultHtmlBaseId++).toString();
    this.subject$.next(alert);
    return alert;
  }

  clearAlert(alert?: ApAlert) {
    if (alert) {
      this.clearSubject$.next(alert);
    }
  }

  clearAllAlerts(id = this.defaultId) {
    this.subject$.next(new ApAlert({ id }));
  }

  clearAllErrorAlerts(id = this.defaultId) {
    this.clearErrorSubject$.next(id);
  }
}
